// src/layouts/configuration/index.js
import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { Card, Tab, Tabs, CircularProgress, Box, Button, Chip, Divider, Snackbar, Alert } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ArgonBox from 'components/ArgonBox';
import ArgonSelect from 'components/ArgonSelect';
import Footer from 'examples/Footer';
import ArgonTypography from 'components/ArgonTypography';
// --- Child Tab Components ---
import PricingRules from './components/pricingRules';
import RevenueBilling from './components/revenueBilling';
import Approval from './components/approval';
import Tasks from './components/tasks';
import Quote from './components/quote';

// --- Contexts & Services ---
import { UserContext } from 'context/userContext';
import { OrgContext } from 'context/orgContext';
import { useNotifications } from 'context/NotificationContext';
import { userOrgs } from 'services/org';
import { getCPQQuoteTemplates } from 'services/quotes';
import { getCPQPriceRules } from 'services/priceRule';
import {
  getCPQGuidedSellingProcess,
  getCPQQuoteTerms,
  deployCPQPricingRules,
  deployCPQQuoteTemplates,
  deployCPQGuidedSellingProcesses,
  deployCPQQuoteTerms,
} from 'services/cpq';
import { useVisibility } from 'context/visibilityContext';

function Configuration() {
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();
  const { isLoadingConfig } = useVisibility();

  const [activeTab, setActiveTab] = useState(0);
  const [loadingOrgs, setLoadingOrgs] = useState(true); // Start true
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [quoteTerms, setQuoteTerms] = useState([]);
  const [priceRules, setPriceRules] = useState([]);
  const [guidedSellingProcesses, setGuidedSellingProcesses] = useState([]);
  const [loadingRelatedData, setLoadingRelatedData] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  // Deploy state
  const [selectedDeployOrgId, setSelectedDeployOrgId] = useState('');
  const [selectedItems, setSelectedItems] = useState({
    pricingRules: [],
    quoteTemplates: [],
    quoteTerms: [],
    guidedSellingProcesses: [],
  });
  const [deployLoading, setDeployLoading] = useState(false);
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // --- REFACTORED: fetchOrgs function ---
  const fetchOrgs = useCallback(async () => {
    if (!userId) return; // Guard against running without a user
    setLoadingOrgs(true);
    try {
      // NEW PATTERN: Service returns data directly or throws. No userId needed.
      const { orgs: fetchedOrgs } = await userOrgs();
      setOrgs(fetchedOrgs || []);
    } catch (error) {
      addNotification({ title: ['Error Fetching Orgs'], details: error.message, severity: 'error' });
      setOrgs([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, [userId, setOrgs, addNotification]);

  useEffect(() => {
    // Fetch orgs only if they are not already in the context
    if (!orgs || orgs.length === 0) {
      fetchOrgs();
    } else {
      setLoadingOrgs(false); // Orgs are already loaded
    }
  }, [orgs, fetchOrgs]);

  const orgOptions = useMemo(() => {
    if (!orgs) return [];
    // Filter for CPQ orgs for this specific configuration page
    return orgs.filter((org) => org.type === 'CPQ').map((org) => ({ value: org.org_id, label: org.name }));
  }, [orgs]);

  // --- REFACTORED: fetchRelatedData function ---
  const fetchRelatedData = useCallback(
    async (orgId) => {
      if (!orgId) return;
      setLoadingRelatedData(true);
      setQuoteTemplates([]);
      setQuoteTerms([]);
      setPriceRules([]);
      setGuidedSellingProcesses([]);
      try {
        // Fetch in parallel for better performance, but handle each independently
        const results = await Promise.allSettled([
          getCPQQuoteTemplates(orgId), // No userId needed
          getCPQQuoteTerms(orgId), // No userId needed
          getCPQPriceRules(orgId), // No userId needed
          getCPQGuidedSellingProcess(orgId), // No userId needed
        ]);

        const [templatesResult, termsResult, rulesResult, guidedSellingResult] = results;

        // Handle quote templates
        if (templatesResult.status === 'fulfilled') {
          setQuoteTemplates(templatesResult.value.quoteTemplates || []);
        } else {
          console.error('[Configuration] Failed to fetch quote templates:', templatesResult.reason);
          addNotification({
            title: ['Warning', 'Quote Templates'],
            details: 'Failed to fetch quote templates: ' + templatesResult.reason.message,
            severity: 'warning',
          });
        }

        // Handle quote terms
        if (termsResult.status === 'fulfilled') {
          console.log('[Configuration] Quote terms response:', termsResult.value);
          setQuoteTerms(termsResult.value.quoteTerms || []);
        } else {
          console.error('[Configuration] Failed to fetch quote terms:', termsResult.reason);
          addNotification({
            title: ['Warning', 'Quote Terms'],
            details: 'Failed to fetch quote terms: ' + termsResult.reason.message,
            severity: 'warning',
          });
        }

        // Handle price rules
        if (rulesResult.status === 'fulfilled') {
          setPriceRules(rulesResult.value.priceRules || []);
        } else {
          console.error('[Configuration] Failed to fetch price rules:', rulesResult.reason);
          addNotification({
            title: ['Warning', 'Price Rules'],
            details: 'Failed to fetch price rules: ' + rulesResult.reason.message,
            severity: 'warning',
          });
        }

        // Handle guided selling processes
        if (guidedSellingResult.status === 'fulfilled') {
          console.log('[Configuration] Guided selling processes response:', guidedSellingResult.value);
          setGuidedSellingProcesses(guidedSellingResult.value.guidedSellingProcesses || []);
        } else {
          console.error('[Configuration] Failed to fetch guided selling processes:', guidedSellingResult.reason);
          addNotification({
            title: ['Warning', 'Guided Selling Processes'],
            details: 'Failed to fetch guided selling processes: ' + guidedSellingResult.reason.message,
            severity: 'warning',
          });
        }
      } catch (error) {
        console.error('[Configuration] Unexpected error in fetchRelatedData:', error);
        addNotification({ title: ['Error Fetching Configuration'], details: error.message, severity: 'error' });
      } finally {
        setLoadingRelatedData(false);
      }
    },
    [addNotification]
  );

  const handleOrgChange = (e) => {
    const newOrgId = e.target.value;
    setSelectedOrgId(newOrgId);
    if (newOrgId) {
      const selectedLabel = orgOptions.find((opt) => opt.value === newOrgId)?.label || newOrgId;
      addNotification({
        title: ['Info', 'Organization Changed'],
        severity: 'info',
        details: `Fetching configurations for: ${selectedLabel}`,
      });
      fetchRelatedData(newOrgId);
    } else {
      // Clear data if no org is selected
      setQuoteTemplates([]);
      setQuoteTerms([]);
      setPriceRules([]);
      setGuidedSellingProcesses([]);
    }
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const selectedOrgType = useMemo(() => {
    if (!selectedOrgId || !orgs) return 'cpq';
    const selectedOrg = orgs.find((org) => org.org_id === selectedOrgId);
    return selectedOrg?.type?.toLowerCase() || 'cpq';
  }, [selectedOrgId, orgs]);

  // Deploy handlers
  const handleItemSelect = (type, items) => {
    setSelectedItems((prev) => ({
      ...prev,
      [type]: items,
    }));
  };

  const handleDeploy = async () => {
    if (!selectedDeployOrgId) {
      addNotification({
        title: ['Error', 'Deploy'],
        details: 'Please select an organization to deploy to',
        severity: 'error',
      });
      return;
    }

    const hasItems = Object.values(selectedItems).some((items) => items.length > 0);
    if (!hasItems) {
      addNotification({
        title: ['Error', 'Deploy'],
        details: 'Please select at least one item to deploy',
        severity: 'error',
      });
      return;
    }

    setDeployLoading(true);
    try {
      // Get the selected org data
      const selectedOrg = orgs.find((org) => org.org_id === selectedDeployOrgId);
      if (!selectedOrg) {
        throw new Error('Selected organization not found');
      }

      const results = [];
      let allSuccessful = true;

      // Deploy Pricing Rules
      if (selectedItems.pricingRules && selectedItems.pricingRules.length > 0) {
        try {
          addNotification({
            title: ['Info', 'Deploy'],
            details: `Deploying ${selectedItems.pricingRules.length} pricing rules...`,
            severity: 'info',
          });

          const result = await deployCPQPricingRules(selectedDeployOrgId, selectedItems.pricingRules, selectedOrg);
          results.push({ type: 'Pricing Rules', success: result.success, message: result.message });

          if (!result.success) allSuccessful = false;
        } catch (error) {
          results.push({ type: 'Pricing Rules', success: false, message: error.message });
          allSuccessful = false;
        }
      }

      // Deploy Quote Templates
      if (selectedItems.quoteTemplates && selectedItems.quoteTemplates.length > 0) {
        try {
          addNotification({
            title: ['Info', 'Deploy'],
            details: `Deploying ${selectedItems.quoteTemplates.length} quote templates...`,
            severity: 'info',
          });

          const result = await deployCPQQuoteTemplates(selectedDeployOrgId, selectedItems.quoteTemplates, selectedOrg);
          results.push({ type: 'Quote Templates', success: result.success, message: result.message });

          if (!result.success) allSuccessful = false;
        } catch (error) {
          results.push({ type: 'Quote Templates', success: false, message: error.message });
          allSuccessful = false;
        }
      }

      // Deploy Quote Terms
      if (selectedItems.quoteTerms && selectedItems.quoteTerms.length > 0) {
        try {
          addNotification({
            title: ['Info', 'Deploy'],
            details: `Deploying ${selectedItems.quoteTerms.length} quote terms...`,
            severity: 'info',
          });

          const result = await deployCPQQuoteTerms(selectedDeployOrgId, selectedItems.quoteTerms, selectedOrg);
          results.push({ type: 'Quote Terms', success: result.success, message: result.message });

          if (!result.success) allSuccessful = false;
        } catch (error) {
          results.push({ type: 'Quote Terms', success: false, message: error.message });
          allSuccessful = false;
        }
      }

      // Deploy Guided Selling Processes
      if (selectedItems.guidedSellingProcesses && selectedItems.guidedSellingProcesses.length > 0) {
        try {
          addNotification({
            title: ['Info', 'Deploy'],
            details: `Deploying ${selectedItems.guidedSellingProcesses.length} guided selling processes...`,
            severity: 'info',
          });

          const result = await deployCPQGuidedSellingProcesses(
            selectedDeployOrgId,
            selectedItems.guidedSellingProcesses,
            selectedOrg
          );
          results.push({ type: 'Guided Selling Processes', success: result.success, message: result.message });

          if (!result.success) allSuccessful = false;
        } catch (error) {
          results.push({ type: 'Guided Selling Processes', success: false, message: error.message });
          allSuccessful = false;
        }
      }

      // Show final results
      if (allSuccessful) {
        // Show both notification and snackbar for success
        addNotification({
          title: ['Success', 'Deploy'],
          details: 'All items deployed successfully!',
          severity: 'success',
        });
        setSnackbarMessage('All items deployed successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);

        // Clear selections after successful deployment
        setSelectedItems({
          pricingRules: [],
          quoteTemplates: [],
          quoteTerms: [],
          guidedSellingProcesses: [],
        });
      } else {
        const failedItems = results
          .filter((r) => !r.success)
          .map((r) => r.type)
          .join(', ');
        
        // Show both notification and snackbar for partial failure
        addNotification({
          title: ['Warning', 'Deploy'],
          details: `Deployment completed with some failures: ${failedItems}`,
          severity: 'warning',
        });
        setSnackbarMessage(`Deployment completed with some failures: ${failedItems}`);
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }

      console.log('Deployment results:', results);
    } catch (error) {
      // Show both notification and snackbar for error
      addNotification({
        title: ['Error', 'Deploy'],
        details: error.message,
        severity: 'error',
      });
      setSnackbarMessage(`Deployment error: ${error.message}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeployLoading(false);
    }
  };

  const childProps = {
    orgs: orgs || [],
    loading: loadingOrgs || isLoadingConfig || loadingRelatedData,
    selectedOrgId: selectedOrgId,
    orgType: selectedOrgType.toLowerCase(),
    guidedSellingProcesses: guidedSellingProcesses,
    priceRules: priceRules,
    quoteTemplates: quoteTemplates,
    quoteTerms: quoteTerms,
    onItemSelect: handleItemSelect,
    selectedItems: selectedItems,
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const renderActiveTab = () => {
    if (childProps.loading && !selectedOrgId) {
      return (
        <ArgonBox display="flex" justifyContent="center" p={5}>
          <CircularProgress />
        </ArgonBox>
      );
    }
    switch (activeTab) {
      case 0:
        return <PricingRules priceRules={priceRules} {...childProps} />;
      case 1:
        return <RevenueBilling {...childProps} />;
      case 2:
        return <Approval {...childProps} />;
      case 3:
        return <Tasks {...childProps} />;
      case 4:
        return <Quote quoteTemplates={quoteTemplates} {...childProps} />;
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            p={2}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <ArgonBox width={{ xs: '100%', sm: 'auto' }} pr={{ xs: 0, sm: 2 }} mb={{ xs: 2, sm: 0 }}>
              <ArgonSelect
                placeholder="Select CPQ Organization..."
                value={selectedOrgId}
                onChange={handleOrgChange}
                options={orgOptions}
                disabled={loadingOrgs || isLoadingConfig}
              />
            </ArgonBox>
            <ArgonBox flexGrow={1}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="configuration tabs">
                <Tab label="Pricing Rules" />
                <Tab label="Revenue Billing" />
                <Tab label="Approval" />
                <Tab label="Tasks" />
                <Tab label="Quote" />
              </Tabs>
            </ArgonBox>
          </ArgonBox>
          <ArgonBox p={3}>{renderActiveTab()}</ArgonBox>
        </Card>

        {/* Persistent Deploy Section */}
        <Card sx={{ mt: 3, position: 'sticky', bottom: 0, zIndex: 1000 }}>
          <ArgonBox p={3}>
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <ArgonBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <ArgonBox mr={2}>
                  <ArgonSelect
                    placeholder="Select Org to Deploy..."
                    value={selectedDeployOrgId}
                    onChange={(e) => setSelectedDeployOrgId(e.target.value)}
                    options={orgOptions}
                    disabled={loadingOrgs || isLoadingConfig}
                  />
                </ArgonBox>

                {/* Selected Items Summary */}
                <ArgonBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  {selectedItems.pricingRules.length > 0 && (
                    <Chip label={`${selectedItems.pricingRules.length} Pricing Rules`} color="primary" size="small" />
                  )}
                  {selectedItems.quoteTemplates.length > 0 && (
                    <Chip
                      label={`${selectedItems.quoteTemplates.length} Quote Templates`}
                      color="secondary"
                      size="small"
                    />
                  )}
                  {selectedItems.quoteTerms.length > 0 && (
                    <Chip
                      label={`${selectedItems.quoteTerms.length} Quote Terms`}
                      color="warning"
                      size="small"
                    />
                  )}
                  {selectedItems.guidedSellingProcesses.length > 0 && (
                    <Chip
                      label={`${selectedItems.guidedSellingProcesses.length} Guided Processes`}
                      color="info"
                      size="small"
                    />
                  )}
                  {Object.values(selectedItems).every((items) => items.length === 0) && (
                    <ArgonTypography variant="caption" color="textSecondary">
                      No items selected
                    </ArgonTypography>
                  )}
                </ArgonBox>
              </ArgonBox>

              <ArgonBox display="flex" alignItems="center" gap={2}>
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => {
                    setSelectedItems({
                      pricingRules: [],
                      quoteTemplates: [],
                      quoteTerms: [],
                      guidedSellingProcesses: [],
                    });
                  }}
                  disabled={Object.values(selectedItems).every((items) => items.length === 0)}
                >
                  Clear All
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  onClick={handleDeploy}
                  disabled={
                    !selectedDeployOrgId ||
                    Object.values(selectedItems).every((items) => items.length === 0) ||
                    deployLoading
                  }
                  startIcon={deployLoading ? <CircularProgress size={16} /> : null}
                >
                  {deployLoading ? 'Deploying...' : 'Deploy'}
                </Button>
              </ArgonBox>
            </ArgonBox>
          </ArgonBox>
        </Card>

        {/* Snackbar for deployment alerts */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            variant="filled"
            sx={{ alignItems: 'center' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </ArgonBox>
    </DashboardLayout>
  );
}

export default Configuration;
